import { Fragment } from "react";
import { useSelector } from "react-redux";
import { Circle } from "lucide-react";
import { LayoutDashboard, Megaphone, UsersRound, UserRound, ClipboardCheck, Dumbbell, Settings } from "lucide-react";
import { Building, NotepadText, LayoutList, MessageSquareText, Banknote, Mail, MousePointerClick } from "lucide-react";
import { ChartBarIcon, DocumentPlusIcon, DocumentTextIcon, ForwardIcon, CameraIcon, PlusCircleIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { ff, flags } from 'functions/roleBasedAccessUtils'
import { useIsTouchDevice } from "redux/customHooks/useIsTouchDevice";

import OrganizationOverrideBanner from 'components/Admin/OrganizationOverrideBanner'
import SimulationClockBanner from 'components/Admin/SimulationClockBanner'
import ResumeWorkoutHeader from "components/Header/ResumeWorkoutHeader";
import ResumeWorkoutHeaderV5 from "components/Header/ResumeWorkoutHeaderV5";
import { Sidebar, SidebarBody, SidebarLink } from "components/ui/sidebar";
import { CollapsibleSidebarOptions } from "components/Sidebar/CollapsibleSidebarOptions";

export default function ScreenFrame({
    children,
    limitWidth = true,
    contentClassName = '',
    alwaysHideResumeWorkoutHeader = false,
    onMouseUp = () => {}
}) {

    const userOnboarded = useSelector((state) => state.user.onboarded);
    const userType = useSelector((state) => state.user.type)
    const adminToolsVisible = useSelector((state) => state.user.adminToolsVisible)
    const enableCoachRunningWorkouts = useSelector((state) => state.user.enableCoachRunningWorkouts)
    const enableKinesisUserGroups = useSelector((state) => state.user.enableKinesisUserGroups)
    const simulationClockDayOffset = useSelector((state) => state.simulationClock.simulationClockDayOffset)
    const overrideOrganizationId = useSelector((state) => state.admin.overrideOrganizationId)
    
    const canAccessKinesisUserGroups = ff(flags.canAccessKinesisUserGroups, { userType: userType, enableKinesisUserGroups: enableKinesisUserGroups })
    const canAccessSidebarSalesTools = ff(flags.canAccessSidebarSalesTools, { userType: userType, adminToolsVisible: adminToolsVisible })
    const canAccessSidebarAdminTools = ff(flags.canAccessSidebarAdminTools, { userType: userType, adminToolsVisible: adminToolsVisible })
    const canRunWorkouts = ff(flags.canRunWorkouts, { userType: userType, enableCoachRunningWorkouts: enableCoachRunningWorkouts })

    const isTouchDevice = useIsTouchDevice();

    const unonboardedSideBarLinks = [
        {
            label: isTouchDevice ? 'Onboard' : 'Complete Onboarding',
            href: "/",
            icon: <Circle className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />
        }
    ]
    
    const sideBarLinks = [
        {
            label: "Home",
            href: "/",
            icon: <LayoutDashboard className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        },
        {
            label: 'Coaches',
            href: '/coaches',
            icon: <Megaphone className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        },
        {
            label: 'Groups',
            href: '/groups',
            icon: <UsersRound className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: canAccessKinesisUserGroups
        },
        {
            label: "Athletes",
            href: "/athletes",
            icon: <UserRound className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        },
        {
            label: isTouchDevice ? 'Assess' : 'Assessments',
            href: '/assessments',
            icon: <ClipboardCheck className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        },
        {
            label: 'Workouts',
            href: '/workouts',
            icon: <Dumbbell className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        },
        {
            label: 'Resources',
            href: '/resources',
            icon: <DocumentTextIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        },
        {
            label: 'Settings',
            href: '/settings',
            icon: <Settings className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
            visible: true
        }
    ];

    const salesSidebarLinks = [
        {
            label: 'Feedback',
            href: '/feedback',
            icon: <MessageSquareText className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'App Store Subscriptions',
            href: '/appStoreSubscriptionDirectory',
            icon: <Banknote className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Branch Clicks',
            href: '/branchClicks',
            icon: <MousePointerClick className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Email Invites',
            href: '/email/invites',
            icon: <Mail className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Internal Reports',
            href: '/reporting/overview',
            icon: <ChartBarIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        }
    ]

    const dataSidebarLinks = [
        {
            label: 'Organizations',
            href: '/organizations',
            icon: <Building className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        // TODO: Add Groups
        {
            label: 'Users',
            href: '/users',
            icon: <UserRound className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Programs',
            href: '/programs',
            icon: <NotepadText className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        // TODO: Add assessments
        // TODO: Add workouts
        // TODO: Add workout sets
    ]

    const programGenerationSidebarLinks = [
        {
            label: 'Boilerplate Programs',
            href: '/boilerplates',
            icon: <SparklesIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Exercise Collections',
            href: '/exerciseCollections',
            icon: <SparklesIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Exercises',
            href: '/exercises',
            icon: <LayoutList className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Create Custom Exercise',
            href: '/exercise/create',
            icon: <PlusCircleIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Legacy Program Generator',
            href: '/program/snapshot/generator',
            icon: <CameraIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        {
            label: 'Legacy Periodization Generator',
            href: '/program/periodization/generator',
            icon: <ForwardIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        },
        
        { 
            label: 'Legacy Workout Builder', 
            href: '/workout/template/create',
            icon: <DocumentPlusIcon className={`text-neutral-200 flex-shrink-0 ${isTouchDevice ? "h-8 w-8" : "h-5 w-5"}`} />,
        }
    ]

    const adminBannerOffsetPx = (
        (simulationClockDayOffset !== 0 ? 52 : 0) + 
        (overrideOrganizationId !== null ? 44 : 0)
    )

    return (
        <>
            {simulationClockDayOffset !== 0 && <SimulationClockBanner />}
            
            {overrideOrganizationId !== null && <OrganizationOverrideBanner />}

            <div
                className='fixed h-full w-full flex bg-zinc-800 py-1.5 pr-1.5'
                style={adminBannerOffsetPx !== 0 ? { paddingTop: adminBannerOffsetPx } : undefined}
                onMouseUp={onMouseUp}
            >
                <Sidebar>
                    <SidebarBody isTouchDevice={isTouchDevice}>
                        <div 
                            className={`
                                overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]
                                ${isTouchDevice ? "space-y-1" : ""}
                            `}
                        >
                            {userOnboarded === false && 
                                <>
                                    {unonboardedSideBarLinks.map((link, index) => (
                                        <SidebarLink key={index} link={link} isTouchDevice={isTouchDevice} />
                                    ))}
                                </>
                            }

                            {userOnboarded === true && 
                                <>
                                    {sideBarLinks.map((link, index) => (
                                        <Fragment key={index}>
                                            {link.visible && <SidebarLink link={link} isTouchDevice={isTouchDevice} />}
                                        </Fragment>
                                    ))}

                                    {(canAccessSidebarSalesTools || canAccessSidebarAdminTools) &&
                                        <div className="h-[1px] w-full my-3 bg-neutral-500"/>
                                    }

                                    {canAccessSidebarSalesTools &&
                                        <CollapsibleSidebarOptions 
                                            label="Sales"
                                            links={salesSidebarLinks}
                                            isTouchDevice={isTouchDevice}
                                        />
                                    }

                                    {canAccessSidebarAdminTools &&
                                        <CollapsibleSidebarOptions 
                                            label="Data"
                                            links={dataSidebarLinks}
                                            isTouchDevice={isTouchDevice}
                                        />
                                    }

                                    {canAccessSidebarAdminTools &&
                                        <CollapsibleSidebarOptions 
                                            label="Program Generation"
                                            links={programGenerationSidebarLinks}
                                            isTouchDevice={isTouchDevice}
                                        />
                                    }
                                </>
                            }
                        </div>
                    </SidebarBody>
                </Sidebar>

                <div className='flex-grow flex flex-col h-full min-h-0 bg-gray-100 rounded-md overflow-hidden'>
                    {canRunWorkouts && alwaysHideResumeWorkoutHeader === false &&
                        <div className='shrink-0'>
                            <ResumeWorkoutHeader />
                            <ResumeWorkoutHeaderV5 />
                        </div>
                    }

                    <div className='min-h-0 flex-1 overflow-y-auto p-5 2xl:p-6 webkit-scrollbar-hidden [-ms-overflow-style:none] [scrollbar-width:none]'>
                        <div
                            className={`
                                space-y-4
                                ${limitWidth ? 'max-w-7xl mx-auto' : ''}
                                ${contentClassName}
                            `}
                        >
                            {children}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
